import React from 'react';

export function applyShippingPromo(baseFee, subtotal, shippingPromo, deliveryType = 'delivery') {
  const b = Number(baseFee || 0);
  const s = Number(subtotal || 0);
  const result = {
    finalFee: +b.toFixed(2),
    originalFee: +b.toFixed(2),
    activeTier: null,
    nextTier: null,
    amountToNext: 0,
    savings: 0,
  };
  if (!shippingPromo || !shippingPromo.enabled) return result;
  if (deliveryType === 'pickup' && !shippingPromo.apply_to_pickup) return result;
  const tiers = (Array.isArray(shippingPromo.tiers) ? shippingPromo.tiers : [])
    .map((t) => ({ ...t, min_subtotal: Number(t.min_subtotal || 0) }))
    .filter((t) => t.min_subtotal >= 0)
    .sort((a, b) => a.min_subtotal - b.min_subtotal);
  if (!tiers.length) return result;
  let active = null;
  for (const t of tiers) {
    if (s >= t.min_subtotal) active = t; else break;
  }
  const next = tiers.find((t) => s < t.min_subtotal) || null;
  if (next) {
    result.nextTier = next;
    result.amountToNext = +Math.max(0, next.min_subtotal - s).toFixed(2);
  }
  if (!active) return result;
  const dtype = (active.discount_type || 'free').toLowerCase();
  const dval = Number(active.discount_value || 0);
  let newFee = b;
  if (dtype === 'free') newFee = 0;
  else if (dtype === 'percentage') {
    const pct = Math.max(0, Math.min(100, dval));
    newFee = b * (1 - pct / 100);
  } else if (dtype === 'fixed') {
    newFee = Math.max(0, b - Math.max(0, dval));
  } else newFee = 0;
  result.finalFee = +newFee.toFixed(2);
  result.activeTier = active;
  result.savings = +(b - newFee).toFixed(2);
  return result;
}

/**
 * Componente visual: barra de progresso ou badge de desbloqueio.
 * Não usa hooks — pode ser chamado inline sem quebrar Rules of Hooks.
 */
export function FreeShippingProgress({ subtotal, shippingPromo, deliveryType = 'delivery', baseFee = 0 }) {
  if (!shippingPromo || !shippingPromo.enabled) return null;
  if (!shippingPromo.show_progress_bar) return null;
  if (deliveryType === 'pickup' && !shippingPromo.apply_to_pickup) return null;
  const tiers = (Array.isArray(shippingPromo.tiers) ? shippingPromo.tiers : [])
    .map((t) => ({ ...t, min_subtotal: Number(t.min_subtotal || 0) }))
    .filter((t) => t.min_subtotal >= 0)
    .sort((a, b) => a.min_subtotal - b.min_subtotal);
  if (!tiers.length) return null;
  const r = applyShippingPromo(baseFee, subtotal, shippingPromo, deliveryType);
  const topTier = tiers[tiers.length - 1];
  const s = Number(subtotal || 0);
  // Se atingiu a maior faixa → badge "desbloqueado"
  if (s >= topTier.min_subtotal) {
    const label = r.activeTier?.label || topTier.label || 'Frete Grátis';
    return (
      <div
        data-testid="shipping-promo-unlocked"
        className="mx-3 my-2 p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 flex items-center gap-2"
      >
        <span className="text-2xl">🎁</span>
        <div className="flex-1">
          <p className="text-sm font-bold text-green-700">
            {label}{String(label).endsWith('!') ? '' : ' desbloqueado!'}
          </p>
          {r.savings > 0 && (
            <p className="text-xs text-green-600">Você economizou R$ {r.savings.toFixed(2).replace('.', ',')}</p>
          )}
        </div>
      </div>
    );
  }
  // Barra de progresso para próxima faixa
  const next = r.nextTier || tiers[0];
  const min = next.min_subtotal;
  const percent = Math.max(0, Math.min(100, min > 0 ? (s / min) * 100 : 0));
  const label = next.label || `${next.discount_type === 'free' ? 'Frete Grátis' : (next.discount_type === 'percentage' ? `${next.discount_value}% off no frete` : `R$ ${Number(next.discount_value).toFixed(2)} off`)}`;
  return (
    <div
      data-testid="shipping-promo-progress"
      className="mx-3 my-2 p-3 rounded-lg bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200"
    >
      <p className="text-xs font-semibold text-purple-800 mb-1.5">
        Faltam <b>R$ {r.amountToNext.toFixed(2).replace('.', ',')}</b> para {label}
      </p>
      <div className="h-2 w-full bg-white/70 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-[10px] text-gray-500 mt-1">R$ {s.toFixed(2).replace('.', ',')} / R$ {min.toFixed(2).replace('.', ',')}</p>
    </div>
  );
}

// ============================================================
// COMPONENTE: NeighborhoodSelector
// Seletor de bairros com busca e visual moderno
